/**
 * AlertsBadge — a count beside the Summary tab when the active portfolio's
 * last alert check (alertsStore.ts, published by Performance Summary) fired
 * any rule. Absent until the Summary page has checked once this session, and
 * absent when nothing fired. One component for the desktop sub-row and the
 * mobile drawer, as RebalanceBadge is.
 */
import { useSyncExternalStore } from 'react';
import { useActivePortfolio } from '@/lib/activePortfolio';
import { firedCount, getAlertCheck, subscribeAlertChecks } from '@/lib/alertsStore';
import { cn } from '@/lib/utils';

export function alertsBadgeLabel(n: number): string {
  return `${n} alert${n === 1 ? '' : 's'} fired`;
}

export function AlertsBadge({ className }: { className?: string }) {
  const { activePortfolioId } = useActivePortfolio();
  const check = useSyncExternalStore(
    subscribeAlertChecks,
    () => (activePortfolioId ? getAlertCheck(activePortfolioId) : null),
    () => null,
  );
  const n = firedCount(check);
  if (n === 0) return null;
  const label = alertsBadgeLabel(n);
  return (
    <span
      role="status"
      aria-label={label}
      title={label}
      className={cn(
        'ml-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 align-middle text-[10px] font-semibold leading-none text-primary-foreground',
        className,
      )}
    >
      {n}
    </span>
  );
}
